(() => {
  'use strict';
  const FILTER_PRIORITY = ['size', 'occasion', 'fabric', 'price', 'colour', 'color', 'availability'];
  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const WISHLIST_KEY = 'behuli:wishlist:v2';
  const WISHLIST_TTL = 180 * 24 * 60 * 60 * 1000;
  const DELIVERY_KEY = 'behuli:delivery-postcode:v1';

  function reorderFilters() {
    document.querySelectorAll('.facets__disclosure, .mobile-facets__details').forEach((facet) => {
      const label = normalize(facet.querySelector('.facets__summary-label, .mobile-facets__summary, summary')?.textContent);
      let order = FILTER_PRIORITY.findIndex((name) => label.includes(name));
      if (order < 0) order = 50;
      facet.style.setProperty('--behuli-filter-order', String(order + 1));
    });
    document.querySelectorAll('.facets__wrapper, .mobile-facets__main').forEach((wrapper) => {
      [...wrapper.children]
        .filter((node) => node.matches?.('.facets__disclosure, .mobile-facets__details'))
        .sort((a, b) => Number(a.style.getPropertyValue('--behuli-filter-order') || 50) - Number(b.style.getPropertyValue('--behuli-filter-order') || 50))
        .forEach((facet) => wrapper.appendChild(facet));
    });
  }

  function markFloatingOverlays() {
    const terms = ['find my size', 'shop with ai', 'chat with us', 'whatsapp'];
    document.querySelectorAll('button, a, [role="button"], iframe').forEach((node) => {
      const label = normalize([node.textContent, node.getAttribute('aria-label'), node.getAttribute('title')].filter(Boolean).join(' '));
      if (!terms.some((term) => label.includes(term))) return;
      let target = node;
      let current = node;
      while (current && current !== document.body) {
        const position = getComputedStyle(current).position;
        if (position === 'fixed' || position === 'sticky') { target = current; break; }
        current = current.parentElement;
      }
      if (getComputedStyle(target).position === 'fixed' || target.tagName === 'IFRAME') target.classList.add('behuli-floating-overlay');
    });
  }

  function improveImageAlternatives() {
    document.querySelectorAll('.card-wrapper img:not([alt]), .card-wrapper img[alt=""]').forEach((image) => {
      const title = image.closest('.card-wrapper')?.querySelector('.card__heading, .card-information__text')?.textContent?.trim();
      if (title) image.alt = title;
    });
    const productTitle = document.querySelector('.product__title h1, main h1')?.textContent?.trim();
    if (productTitle) document.querySelectorAll('.product__media img:not([alt]), .product__media img[alt=""]').forEach((image) => { image.alt = productTitle; });
  }

  function syncCartState() {
    const isOpen = Boolean(document.querySelector('cart-drawer.active, cart-drawer .drawer.active'));
    document.body.classList.toggle('behuli-cart-drawer-open', isOpen);
    document.querySelectorAll('.behuli-floating-overlay').forEach((node) => {
      if (isOpen) node.setAttribute('aria-hidden', 'true'); else node.removeAttribute('aria-hidden');
    });
  }

  function readWishlist() {
    try {
      const now = Date.now();
      const items = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]').filter((item) => item?.handle && (!item.savedAt || now - item.savedAt < WISHLIST_TTL));
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items.slice(0, 24)));
      return items.slice(0, 24);
    } catch (_) { return []; }
  }

  function writeWishlist(items) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(items.slice(0, 24))); } catch (_) {}
    updateWishlistUi();
  }

  function isSaved(handle) { return readWishlist().some((item) => item.handle === handle); }

  async function productSnapshot(handle, fallback = {}) {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Product unavailable');
      const p = await response.json();
      return { handle: p.handle, title: p.title, url: `${window.Shopify?.routes?.root || '/'}products/${p.handle}`, image: p.featured_image || '', price: p.price_min || p.price || 0, savedAt: Date.now() };
    } catch (_) {
      return { handle, title: fallback.title || handle, url: fallback.url || `/products/${handle}`, image: fallback.image || '', price: Number(fallback.price || 0), savedAt: Date.now() };
    }
  }

  async function toggleWishlist(button) {
    const handle = button.dataset.wishlistHandle;
    if (!handle) return;
    let items = readWishlist();
    if (items.some((item) => item.handle === handle)) {
      items = items.filter((item) => item.handle !== handle);
      writeWishlist(items);
      return;
    }
    const snapshot = await productSnapshot(handle, button.dataset);
    writeWishlist([snapshot, ...items.filter((item) => item.handle !== handle)]);
  }

  function injectCardWishlistButtons() {
    document.querySelectorAll('.card-wrapper.product-card-wrapper').forEach((card) => {
      if (card.querySelector('[data-wishlist-handle]')) return;
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      const match = link.getAttribute('href')?.match(/\/products\/([^?#]+)/);
      if (!match) return;
      const handle = match[1];
      const title = card.querySelector('.card__heading')?.textContent?.trim() || '';
      const image = card.querySelector('.card__media img')?.currentSrc || card.querySelector('.card__media img')?.src || '';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'behuli-wishlist-heart';
      button.dataset.wishlistHandle = handle;
      button.dataset.title = title;
      button.dataset.url = link.href;
      button.dataset.image = image;
      button.setAttribute('aria-label', `Save ${title || 'product'} for later`);
      button.innerHTML = '<span aria-hidden="true">♡</span>';
      (card.querySelector('.card__inner') || card).appendChild(button);
    });
  }

  function ensureWishlistDrawer() {
    if (document.getElementById('BehuliWishlistDrawer')) return;
    document.body.insertAdjacentHTML('beforeend', `<div class="behuli-wishlist-shell" id="BehuliWishlistDrawer" hidden><button class="behuli-wishlist-backdrop" type="button" data-wishlist-close aria-label="Close saved items"></button><aside class="behuli-wishlist-drawer" role="dialog" aria-modal="true" aria-labelledby="BehuliWishlistTitle"><div class="behuli-wishlist-head"><div><span class="behuli-wishlist-kicker">BEHULI</span><h2 id="BehuliWishlistTitle">Saved for later</h2></div><button type="button" class="behuli-wishlist-close" data-wishlist-close aria-label="Close">×</button></div><div class="behuli-wishlist-list" data-wishlist-list></div></aside></div>`);
  }

  function ensureWishlistHeaderButton() {
    if (document.querySelector('[data-wishlist-open]')) return;
    const icons = document.querySelector('.header__icons');
    if (!icons) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'header__icon header__icon--wishlist link focus-inset';
    button.setAttribute('data-wishlist-open', '');
    button.setAttribute('aria-label', 'Saved items');
    button.innerHTML = '<span class="behuli-header-heart" aria-hidden="true">♡</span><span class="behuli-wishlist-count" data-wishlist-count hidden>0</span>';
    icons.insertBefore(button, icons.querySelector('.header__icon--cart') || null);
  }

  function formatMoney(cents) {
    try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency: window.Shopify?.currency?.active || 'AUD' }).format((Number(cents) || 0) / 100); }
    catch (_) { return `$${((Number(cents) || 0) / 100).toFixed(2)}`; }
  }

  function renderWishlistDrawer() {
    ensureWishlistDrawer();
    const list = document.querySelector('[data-wishlist-list]');
    if (!list) return;
    const items = readWishlist();
    if (!items.length) {
      list.innerHTML = '<div class="behuli-wishlist-empty"><span aria-hidden="true">♡</span><p>Your saved pieces will appear here.</p><a href="/collections/all">Continue shopping</a></div>';
      return;
    }
    list.innerHTML = items.map((item) => `<article class="behuli-wishlist-item"><a class="behuli-wishlist-image" href="${item.url}">${item.image ? `<img src="${item.image}" alt="" loading="lazy">` : ''}</a><div class="behuli-wishlist-copy"><a href="${item.url}">${item.title}</a><strong>${formatMoney(item.price)}</strong><button type="button" data-wishlist-remove="${item.handle}">Remove</button></div></article>`).join('');
  }

  function updateWishlistUi() {
    const items = readWishlist();
    const handles = new Set(items.map((item) => item.handle));
    document.querySelectorAll('[data-wishlist-handle]').forEach((button) => {
      const saved = handles.has(button.dataset.wishlistHandle);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const icon = button.querySelector('[aria-hidden="true"]');
      if (icon) icon.textContent = saved ? '♥' : '♡';
      const label = button.querySelector('[data-wishlist-label]');
      if (label) label.textContent = saved ? 'Saved' : 'Save for later';
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    });
    if (!document.getElementById('BehuliWishlistDrawer')?.hidden) renderWishlistDrawer();
  }

  function openWishlist() {
    renderWishlistDrawer();
    const shell = document.getElementById('BehuliWishlistDrawer');
    if (!shell) return;
    shell.hidden = false;
    document.body.classList.add('behuli-wishlist-open');
    shell.querySelector('[data-wishlist-close]')?.focus();
  }

  function closeWishlist() {
    const shell = document.getElementById('BehuliWishlistDrawer');
    if (shell) shell.hidden = true;
    document.body.classList.remove('behuli-wishlist-open');
  }

  function restoreDeliveryEstimator() {
    document.querySelectorAll('behuli-delivery-estimator').forEach((estimator) => {
      if (estimator.dataset.memoryInit) return;
      estimator.dataset.memoryInit = 'true';
      const form = estimator.querySelector('[data-delivery-form]');
      const input = estimator.querySelector('[data-delivery-postcode]');
      if (!form || !input) return;
      form.addEventListener('submit', () => {
        const value = input.value.trim();
        if (/^\d{4}$/.test(value)) {
          try { localStorage.setItem(DELIVERY_KEY, value); } catch (_) {}
        }
      });
      let remembered = '';
      try { remembered = localStorage.getItem(DELIVERY_KEY) || ''; } catch (_) {}
      if (/^\d{4}$/.test(remembered) && !input.value) {
        input.value = remembered;
        setTimeout(() => form.requestSubmit(), 50);
      }
    });
  }

  function recommendationCategoryAllowed(anchor, candidate) {
    const source = normalize(`${anchor.product_type || ''} ${anchor.product_title || ''}`);
    const target = normalize(`${candidate.type || ''} ${candidate.title || ''}`);
    let allowed = [];
    if (source.includes('saree')) allowed = ['blouse', 'bangle', 'earring', 'necklace', 'choker', 'potey', 'bindi'];
    else if (source.includes('kurti') || source.includes('kurta') || source.includes('anarkali') || source.includes('palazzo') || source.includes('sharara')) allowed = ['bangle', 'earring', 'necklace', 'choker', 'potey', 'bindi'];
    else return true;
    return allowed.some((word) => target.includes(word));
  }

  async function getCartRecommendations(anchor, existingIds) {
    const root = window.Shopify?.routes?.root || '/';
    for (const intent of ['complementary', 'related']) {
      try {
        const response = await fetch(`${root}recommendations/products.json?product_id=${anchor.product_id}&limit=8&intent=${intent}`);
        if (!response.ok) continue;
        const data = await response.json();
        const products = (data.products || []).filter((product) => product.available && !existingIds.has(Number(product.id)) && recommendationCategoryAllowed(anchor, product));
        if (products.length) return products.slice(0, 2);
      } catch (_) {}
    }
    return [];
  }

  async function refreshCartUpsell() {
    const drawer = document.querySelector('cart-drawer #CartDrawer');
    if (!drawer || drawer.dataset.upsellBusy === 'true') return;
    drawer.dataset.upsellBusy = 'true';
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      const old = drawer.querySelector('.behuli-cart-upsell');
      if (!cart.items?.length) { old?.remove(); return; }
      const existingIds = new Set(cart.items.map((item) => Number(item.product_id)));
      const anchor = cart.items.find((item) => !/earring|bangle|necklace|choker|bindi|potey|jewell/i.test(`${item.product_type || ''} ${item.product_title || ''}`)) || cart.items[0];
      const products = await getCartRecommendations(anchor, existingIds);
      if (!products.length) { old?.remove(); return; }
      const html = `<section class="behuli-cart-upsell"><div class="behuli-cart-upsell__head"><strong>Complete your look</strong><span>Picked to complement your cart</span></div><div class="behuli-cart-upsell__rail">${products.map((product) => { const variant = (product.variants || []).find((v) => v.available); const direct = (product.variants || []).filter((v) => v.available).length === 1; return `<article><a href="/products/${product.handle}">${product.featured_image ? `<img src="${product.featured_image}" alt="" loading="lazy">` : ''}</a><div><a href="/products/${product.handle}">${product.title}</a><strong>${formatMoney(product.price_min || product.price)}</strong>${variant && direct ? `<button type="button" data-cart-upsell-add="${variant.id}">Add</button>` : `<a class="behuli-cart-upsell__choose" href="/products/${product.handle}">Choose</a>`}</div></article>`; }).join('')}</div></section>`;
      if (old) old.outerHTML = html;
      else drawer.querySelector('.drawer__footer')?.insertAdjacentHTML('beforebegin', html);
    } finally { drawer.dataset.upsellBusy = 'false'; }
  }

  async function addCartUpsell(variantId, button) {
    if (button.disabled) return;
    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'Adding…';
    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error();
      const parser = new DOMParser();
      if (state.sections?.['cart-drawer']) {
        const source = parser.parseFromString(state.sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
        const target = document.querySelector('#CartDrawer');
        if (source && target) target.innerHTML = source.innerHTML;
      }
      if (state.sections?.['cart-icon-bubble']) {
        const source = parser.parseFromString(state.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
        const target = document.querySelector('#cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      setTimeout(refreshCartUpsell, 100);
    } catch (_) {
      button.textContent = 'Try again';
      setTimeout(() => { button.textContent = original; button.disabled = false; }, 1800);
    }
  }

  document.addEventListener('click', (event) => {
    const wish = event.target.closest('[data-wishlist-handle]');
    if (wish) { event.preventDefault(); event.stopPropagation(); toggleWishlist(wish); return; }
    if (event.target.closest('[data-wishlist-open]')) { event.preventDefault(); openWishlist(); return; }
    if (event.target.closest('[data-wishlist-close]')) { event.preventDefault(); closeWishlist(); return; }
    const remove = event.target.closest('[data-wishlist-remove]');
    if (remove) { writeWishlist(readWishlist().filter((item) => item.handle !== remove.dataset.wishlistRemove)); renderWishlistDrawer(); return; }
    const upsell = event.target.closest('[data-cart-upsell-add]');
    if (upsell) { event.preventDefault(); addCartUpsell(upsell.dataset.cartUpsellAdd, upsell); }
  });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeWishlist(); });
  document.addEventListener('cart:refresh', () => setTimeout(refreshCartUpsell, 120));

  let scheduled = false;
  function refresh() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      reorderFilters(); markFloatingOverlays(); improveImageAlternatives(); syncCartState();
      injectCardWishlistButtons(); ensureWishlistHeaderButton(); ensureWishlistDrawer(); updateWishlistUi(); restoreDeliveryEstimator();
      if (document.querySelector('cart-drawer.active, cart-drawer .drawer.active')) refreshCartUpsell();
    });
  }

  document.addEventListener('DOMContentLoaded', refresh);
  document.addEventListener('shopify:section:load', refresh);
  window.addEventListener('storage', (event) => { if (event.key === WISHLIST_KEY) updateWishlistUi(); });
  new MutationObserver(refresh).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open'] });
})();
